import type { TemplateStatus, TemplateType } from '@piccolohealth/pbs-common';
import {
  arrayOfParam,
  numberParam,
  stringParam,
  useDebouncedCallback,
  useQueryParams,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import type { PaginationFilter } from '../../../graphql/fetcher';

const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_NAME_FILTER = '';
const DEFAULT_STATUS_FILTER: TemplateStatus[] = [];
const DEFAULT_TYPE_FILTER: TemplateType[] = [];
const DEFAULT_LABEL_FILTER: string[] = [];

export interface TemplatesFilter extends PaginationFilter {
  nameFilter: string;
  statusFilter: TemplateStatus[] | undefined;
  typeFilter: TemplateType[] | undefined;
  labelFilter: string[] | undefined;
  activeFilterCount: number;
  onNameFilter: (name: string) => void;
  onStatusFilter: (statuses?: TemplateStatus[]) => void;
  onTypeFilter: (types?: TemplateType[]) => void;
  onLabelFilter: (labels?: string[]) => void;
  reset: () => void;
}

export const useTemplatesFilter: () => TemplatesFilter = () => {
  const [params, setParams] = useQueryParams({
    page: numberParam,
    pageSize: numberParam,
    name: stringParam,
    status: arrayOfParam(stringParam, { delimiter: ',' }),
    type: arrayOfParam(stringParam, { delimiter: ',' }),
    label: arrayOfParam(stringParam, { delimiter: ',' }),
  });

  const setCurrentPageNumber = (value: number) =>
    setParams({
      page: value,
    });

  const setPageSize = (value: number) =>
    setParams({
      pageSize: value,
    });

  const onNameFilter = useDebouncedCallback(
    (value: string) =>
      setParams({
        name: value,
        page: 0,
      }),
    1000,
  );

  const onStatusFilter = (statuses?: TemplateStatus[]) =>
    setParams({
      status: statuses,
      page: 0,
    });

  const onTypeFilter = (types?: TemplateType[]) =>
    setParams({
      type: types,
      page: 0,
    });

  const onLabelFilter = (labels?: string[]) =>
    setParams({
      label: labels,
      page: 0,
    });

  const showTotal = (total: number, range: [number, number]) =>
    `${range[0]}-${range[1]} of ${total} templates`;

  const reset = () => {
    setParams({
      page: DEFAULT_PAGE_NUMBER,
      pageSize: DEFAULT_PAGE_SIZE,
      name: DEFAULT_NAME_FILTER,
      status: DEFAULT_STATUS_FILTER,
      type: DEFAULT_TYPE_FILTER,
      label: DEFAULT_LABEL_FILTER,
    });
  };

  const activeFilterCount = P.compact([
    params.name,
    params.status,
    params.type,
    params.label,
  ]).length;

  return {
    pageSizeOptions: [25, 50, 100],
    nameFilter: params.name ?? DEFAULT_NAME_FILTER,
    statusFilter: (params.status as TemplateStatus[]) ?? DEFAULT_STATUS_FILTER,
    typeFilter: (params.type as TemplateType[]) ?? DEFAULT_TYPE_FILTER,
    labelFilter: (params.label as string[]) ?? DEFAULT_LABEL_FILTER,
    pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
    currentPageNumber: params.page ?? DEFAULT_PAGE_NUMBER,
    activeFilterCount,
    showTotal,
    setPageSize,
    setCurrentPageNumber,
    onNameFilter,
    onStatusFilter,
    onTypeFilter,
    onLabelFilter,
    reset,
  };
};
